// Firebase Analytics for the deeplink landing page — B2CAPPTH-2964 Section 3.
//
// The spec lists `deeplink_click` and `app_store_redirect` as Server-side, and
// `trackEvent` in api.js posts them to be-deeplink accordingly. That service is
// not deployed: /deeplink/v1/tracking/event answers 403 and /resolve answers
// 404, so both events have produced no data at all, while the six client-side
// events in the mobile app have been verified end to end. This module fills the
// top of the funnel from the browser instead, reporting into the SAME Firebase
// project the app reports into, so the two halves join up.
//
// Two things to know when reading the numbers this produces:
//
//  - Browser-side counting is blockable. Ad blockers and tracking prevention
//    drop these requests, so this is a floor on real clicks, where the
//    server-side count would not have been. That is very likely why the spec
//    put these two on the server in the first place.
//
//  - When be-deeplink ships, `trackEvent` will start succeeding as well and the
//    same click will be counted twice. Whoever owns that rollout has to switch
//    one side off. Both sides are called from the one function in api.js so
//    there is a single place to do it.

let analyticsPromise = null;

/**
 * The Web app config, read from REACT_APP_* like every other setting here.
 *
 * Deliberately not hard-coded: production needs its own Web app in the
 * production Firebase project, and the UAT values must never be what ships
 * there. While the production values are blank this returns null and the whole
 * module goes quiet — no init, no events, no console noise.
 */
const readConfig = () => {
  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };
  // measurementId is what Analytics reports through and appId identifies the Web
  // app, but apiKey is required too: Analytics resolves an app instance id via
  // Firebase Installations, which throws `installations/missing-app-config-values`
  // without it. Checking all three here turns a runtime throw into staying
  // quiet, which matters because apiKey arrives from a CI secret and is blank
  // in the repo — a build without it should do nothing, not break the page.
  if (!config.apiKey || !config.appId || !config.measurementId) return null;
  return config;
};

/**
 * Load and initialise Firebase once, lazily.
 *
 * Imported dynamically so the SDK lands in its own chunk: the deeplink landing
 * page is a public route that the rest of the admin dashboard never touches,
 * and the dashboard should not carry this weight on every login.
 *
 * `isSupported()` is what keeps this safe in the browsers that cannot run
 * Analytics at all (no IndexedDB, some private modes) — without it the SDK
 * throws on init rather than degrading.
 */
const getAnalyticsInstance = () => {
  if (analyticsPromise) return analyticsPromise;

  const config = readConfig();
  if (!config) {
    analyticsPromise = Promise.resolve(null);
    return analyticsPromise;
  }

  analyticsPromise = Promise.all([
    import("firebase/app"),
    import("firebase/analytics"),
  ])
    .then(([{ initializeApp }, { initializeAnalytics, isSupported }]) =>
      isSupported().then(supported => {
        if (!supported) return null;
        // initializeAnalytics, not getAnalytics: it is the only one that takes
        // gtag config, and debug mode has to be set there. Passing
        // `debug_mode` alongside an event instead just adds a custom parameter
        // named debug_mode — the hit goes out without `_dbg=1` and DebugView
        // never shows it, which is exactly what happened the first time.
        const settings = isDebugMode() ? { config: { debug_mode: true } } : undefined;
        return initializeAnalytics(initializeApp(config), settings);
      })
    )
    .catch(err => {
      // eslint-disable-next-line no-console
      console.warn("[deeplink] firebase analytics unavailable:", err?.message);
      return null;
    });

  return analyticsPromise;
};

/**
 * `?debug_mode=1` on the landing URL routes these events to Firebase DebugView
 * so they can be watched live instead of waiting on the standard reports. Read
 * once at init, because that is where gtag needs it — see getAnalyticsInstance.
 *
 * Worth having explicitly: the mobile app cannot use DebugView at all — it
 * deletes the /google/firebase/debug_mode key on every launch before
 * FIRApp configure reads it — so this page is the one half of the journey where
 * live checking is possible without a special build.
 */
const isDebugMode = () => {
  try {
    return new URLSearchParams(window.location.search).get("debug_mode") === "1";
  } catch (err) {
    return false;
  }
};

/**
 * Send one event to Firebase.
 *
 * Never rejects and never blocks: a click has to reach the app whether or not
 * the measurement lands, which is the same rule `trackEvent` follows.
 * Undefined params are dropped rather than sent as "undefined" strings.
 */
export const logLandingEvent = (eventName, params = {}) => {
  try {
    const payload = { platform_surface: "web_landing" };
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") {
        payload[key] = value;
      }
    });

    return getAnalyticsInstance()
      .then(analytics => {
        if (!analytics) return;
        return import("firebase/analytics").then(({ logEvent }) =>
          logEvent(analytics, eventName, payload)
        );
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.warn("[deeplink] firebase event failed:", eventName, err?.message);
      });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[deeplink] firebase event failed:", eventName, err?.message);
    return Promise.resolve();
  }
};

export default logLandingEvent;
